/* eslint-disable no-empty */
import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Pencil, X, Upload, Loader2 } from 'lucide-react';
import { useProgram } from './ProgramContext';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'g33_program_token';
const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; } };

// ============ Custom cursor (hover-only) ============
export const G33Cursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%,-50%)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%,-50%)`;
      }
    };
    const onOver = (e) => {
      const t = e.target;
      const interactive = t.closest('a, button, [role="button"], input, textarea, [data-interactive]');
      if (ringRef.current) ringRef.current.classList.toggle('is-hover', !!interactive);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseover', onOver);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
    };
  }, []);

  return (
    <>
      <div ref={dotRef} className="g33-cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="g33-cursor-ring" aria-hidden="true" />
    </>
  );
};

// ============ Edit Modal (popup editor for any block) ============
export const EditModal = ({ open, title, value, multiline = false, placeholder = '', helper = '', onClose, onSave }) => {
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (open) setDraft(value || ''); }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 60);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const commit = async () => {
    setSaving(true);
    try { await onSave(draft); onClose(); }
    catch (e) { /* keep open on error */ }
    finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="g33-modal-backdrop"
          data-testid="edit-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="g33-modal"
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            <div className="g33-modal-header">
              <div>
                <div className="g33-eyebrow" style={{ marginBottom: 6 }}>Edit</div>
                <h3 className="g33-display" style={{ fontSize: 24, margin: 0 }}>{title}</h3>
              </div>
              <button onClick={onClose} className="g33-modal-close" aria-label="Close" data-testid="edit-modal-close">
                <X size={18} />
              </button>
            </div>

            {helper && (
              <p style={{ fontSize: 13, color: 'var(--g33-grey-warm)', marginTop: 8, marginBottom: 0, lineHeight: 1.5 }}>
                {helper}
              </p>
            )}

            <div style={{ marginTop: 24 }}>
              {multiline ? (
                <textarea
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={placeholder}
                  rows={Math.min(20, Math.max(6, (draft || '').split('\n').length + 2))}
                  className="g33-modal-input"
                  data-testid="edit-modal-textarea"
                  style={{ minHeight: 200, resize: 'vertical' }}
                />
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={placeholder}
                  onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
                  className="g33-modal-input"
                  data-testid="edit-modal-input"
                />
              )}
            </div>

            <div className="g33-modal-actions">
              <button onClick={onClose} className="g33-btn-ghost" data-testid="edit-modal-cancel">Cancel</button>
              <button
                onClick={commit}
                className="g33-btn-primary"
                disabled={saving}
                data-testid="edit-modal-save"
                style={{ padding: '14px 28px', fontSize: 11 }}
              >
                {saving ? <><Loader2 size={14} className="g33-spin" /> Saving</> : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============ Editable text block (pencil button → modal) ============
// Renders children (rendered content) in display mode.
// In edit mode, overlays a small pencil button. Click → opens EditModal.
export const Editable = ({
  value,
  onSave,
  multiline = false,
  as: Tag = 'span',
  className = '',
  placeholder = '',
  label = 'Content',
  helper = '',
  children = null, // optional pre-rendered display node
  testId = '',
}) => {
  const { editMode } = useProgram();
  const [open, setOpen] = useState(false);

  const display = children !== null ? children : (
    value || (
      <span style={{ color: 'var(--g33-grey-warm)', fontStyle: 'italic' }}>
        {placeholder || 'Click pencil to add content'}
      </span>
    )
  );

  return (
    <span className={`g33-editable-wrap ${editMode ? 'is-edit' : ''}`} data-testid={testId || undefined}>
      <Tag className={className}>
        {display}
      </Tag>
      {editMode && (
        <button
          type="button"
          className="g33-edit-pencil"
          onClick={() => setOpen(true)}
          aria-label={`Edit ${label}`}
          data-testid={testId ? `${testId}-edit-btn` : 'edit-btn'}
          title={`Edit ${label}`}
        >
          <Pencil size={13} />
        </button>
      )}
      <EditModal
        open={open}
        title={label}
        value={value}
        multiline={multiline}
        placeholder={placeholder}
        helper={helper}
        onClose={() => setOpen(false)}
        onSave={onSave}
      />
    </span>
  );
};

// ============ Standalone edit-pill button (for blocks where rendered content can't be wrapped) ============
export const EditButton = ({ value, onSave, multiline = true, label = 'Section', placeholder = '', helper = '', testId = '' }) => {
  const { editMode } = useProgram();
  const [open, setOpen] = useState(false);

  if (!editMode) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="g33-edit-pill"
        data-testid={testId || 'edit-section-btn'}
      >
        <Pencil size={12} /> Edit {label.toLowerCase()}
      </button>
      <EditModal
        open={open}
        title={label}
        value={value}
        multiline={multiline}
        placeholder={placeholder}
        helper={helper}
        onClose={() => setOpen(false)}
        onSave={onSave}
      />
    </>
  );
};

// ============ Reveal-on-scroll ============
export const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay, ease: [0.2, 0.8, 0.2, 1] }}
  >
    {children}
  </motion.div>
);

// ============ Hairline gold divider that draws on scroll ============
export const GoldDivider = ({ className = '' }) => (
  <motion.div
    className={`g33-divider ${className}`}
    style={{ background: 'var(--g33-or)', transformOrigin: 'left center' }}
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
  />
);

// ============ Parallax background image ============
export const ParallaxImage = ({ src, alt = '', strength = 0.3, className = '' }) => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, (v) => v * strength);
  return (
    <div ref={ref} className={className} style={{ overflow: 'hidden', position: 'relative' }}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
};

// ============ Letter-stagger headline ============
// Splits text into words first (no mid-word break), then animates each letter.
export const StaggerHeadline = ({ text, className = '' }) => {
  const words = (text || '').split(' ');
  let letterIndex = 0;
  return (
    <h1 className={className} aria-label={text}>
      {words.map((word, wi) => (
        <span key={wi} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {word.split('').map((c) => {
            const i = letterIndex++;
            return (
              <motion.span
                key={i}
                aria-hidden="true"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.025, ease: [0.2, 0.8, 0.2, 1] }}
                style={{ display: 'inline-block' }}
              >
                {c}
              </motion.span>
            );
          })}
          {wi < words.length - 1 && <span>{'\u00A0'}</span>}
        </span>
      ))}
    </h1>
  );
};

// ============ Editable list (key takeaways, etc.) — modal-based ============
export const EditableList = ({ items = [], onSave, label = 'List', testId = '' }) => {
  const { editMode } = useProgram();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(items);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setDraft(items.length ? items : ['']); }, [open, items]);

  const updateAt = (i, v) => setDraft(d => d.map((x, j) => j === i ? v : x));
  const addItem = () => setDraft(d => [...d, '']);
  const removeAt = (i) => setDraft(d => d.filter((_, j) => j !== i));

  const commit = async () => {
    setSaving(true);
    try {
      const cleaned = draft.map(s => (s || '').trim()).filter(Boolean);
      await onSave(cleaned);
      setOpen(false);
    } catch {} finally { setSaving(false); }
  };

  if (!editMode) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="g33-edit-pill"
        data-testid={testId ? `${testId}-edit-btn` : 'list-edit-btn'}
      >
        <Pencil size={12} /> Edit {label.toLowerCase()}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="g33-modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="g33-modal"
              role="dialog"
              aria-modal="true"
            >
              <div className="g33-modal-header">
                <div>
                  <div className="g33-eyebrow" style={{ marginBottom: 6 }}>Edit</div>
                  <h3 className="g33-display" style={{ fontSize: 24, margin: 0 }}>{label}</h3>
                </div>
                <button onClick={() => setOpen(false)} className="g33-modal-close" aria-label="Close">
                  <X size={18} />
                </button>
              </div>

              <div style={{ marginTop: 24, maxHeight: '50vh', overflowY: 'auto', paddingRight: 8 }}>
                {draft.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ color: 'var(--g33-or)', flexShrink: 0, fontFamily: 'var(--g33-font-display)' }}>›</span>
                    <input
                      type="text"
                      value={t}
                      onChange={(e) => updateAt(i, e.target.value)}
                      className="g33-modal-input"
                      style={{ flex: 1 }}
                      data-testid={`list-item-${i}`}
                    />
                    <button
                      onClick={() => removeAt(i)}
                      className="g33-modal-close"
                      aria-label="Remove"
                      style={{ width: 32, height: 32 }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button onClick={addItem} className="g33-btn-ghost" style={{ marginTop: 8 }}>
                  + Add item
                </button>
              </div>

              <div className="g33-modal-actions">
                <button onClick={() => setOpen(false)} className="g33-btn-ghost">Cancel</button>
                <button
                  onClick={commit}
                  className="g33-btn-primary"
                  disabled={saving}
                  style={{ padding: '14px 28px', fontSize: 11 }}
                  data-testid="list-modal-save"
                >
                  {saving ? <><Loader2 size={14} className="g33-spin" /> Saving</> : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ Trainer Note — CTA button that opens a growing modal ============
export const TrainerNote = ({ note, testId = '' }) => {
  const [open, setOpen] = useState(false);
  if (!note || !note.body) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="g33-note-btn"
        data-testid={testId || 'trainer-note-btn'}
        aria-label={`Open trainer note: ${note.title || note.label || 'Note'}`}
      >
        <span className="g33-note-btn-dot" aria-hidden="true" />
        <span className="g33-note-btn-label">{note.label || 'NOTE'}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="g33-modal-backdrop"
            data-testid="trainer-note-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.86 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.92 }}
              transition={{ duration: 0.42, ease: [0.2, 0.8, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="g33-note-modal"
              role="dialog"
              aria-modal="true"
              aria-label={note.title || 'Trainer note'}
            >
              <div className="g33-note-header">
                <div>
                  {note.eyebrow && (
                    <div className="g33-note-eyebrow">
                      <span className="g33-trainer-tag-dot" aria-hidden="true" />
                      {note.eyebrow}
                    </div>
                  )}
                  {note.title && (
                    <h3 className="g33-display g33-note-title">
                      {note.title}
                    </h3>
                  )}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="g33-modal-close"
                  aria-label="Close"
                  data-testid="trainer-note-close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="g33-note-body">
                {String(note.body).split(/\n\n+/).map((para, i) => {
                  const lines = para.split('\n');
                  const rendered = [];
                  let textBuf = [];
                  const flush = () => {
                    if (textBuf.length) {
                      rendered.push(
                        <p
                          key={`p-${i}-${rendered.length}`}
                          style={{ whiteSpace: 'pre-line', margin: '0 0 20px' }}
                        >
                          {textBuf.join('\n')}
                        </p>
                      );
                      textBuf = [];
                    }
                  };
                  for (const line of lines) {
                    const m = line.trim().match(/^\[GATE33-[A-Z0-9-]+-IMG-\d{2,3}\]$/);
                    if (m) {
                      flush();
                      rendered.push(<CodedImage key={`c-${i}-${rendered.length}`} code={m[0]} />);
                    } else {
                      textBuf.push(line);
                    }
                  }
                  flush();
                  return <div key={i}>{rendered}</div>;
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============ Coded Image / Placeholder ============
// Renders an uploaded image if a mapping exists for the code, otherwise
// shows a visible placeholder rectangle that includes the code (for upload reference).
// Code format: [GATE33-DXX-MYY-LABEL-IMG-NN]
export const CodedImage = ({ code, caption: captionOverride, aspectRatio = '16 / 10', fitMode = 'natural', inRow = false, missing = 'placeholder' }) => {
  const { images } = useProgram();
  const entry = images && images[code];
  const url = entry?.url;
  const absUrl = url && url.startsWith('/') ? `${API}${url}` : url;
  const caption = captionOverride || entry?.caption || '';

  if (absUrl) {
    const isFitted = fitMode === 'contain' || fitMode === 'cover';
    const margin = isFitted || inRow ? 0 : '32px 0';
    return (
      <figure
        className="g33-coded-image"
        data-testid={`coded-image-${code}`}
        data-code={code}
        style={{ margin, width: '100%', height: isFitted ? '100%' : 'auto' }}
      >
        {isFitted ? (
          <div style={{ width: '100%', aspectRatio, background: 'var(--g33-cream, #f5efe6)', borderRadius: 2, overflow: 'hidden' }}>
            <img
              src={absUrl}
              alt={caption || code}
              loading="lazy"
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: fitMode,
              }}
            />
          </div>
        ) : (
          <img
            src={absUrl}
            alt={caption || code}
            loading="lazy"
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              borderRadius: 2,
            }}
          />
        )}
        {caption && (
          <figcaption
            className="g33-eyebrow"
            style={{
              marginTop: 12,
              fontSize: 11,
              letterSpacing: '0.14em',
              color: 'var(--g33-grey-warm)',
              textTransform: 'none',
              fontStyle: 'italic',
            }}
          >
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }

  if (missing === 'raw') {
    return (
      <p className="g33-body" data-testid={`raw-code-${code}`} style={{ margin: inRow ? 0 : '16px 0' }}>
        {code}
      </p>
    );
  }

  // PLACEHOLDER state — show the code clearly so the user knows where to upload.
  return (
    <div
      className="g33-coded-placeholder"
      data-testid={`coded-placeholder-${code}`}
      data-code={code}
      style={{
        margin: '32px 0',
        position: 'relative',
        width: '100%',
        aspectRatio,
        background:
          'repeating-linear-gradient(45deg, rgba(201,169,97,0.06) 0 12px, rgba(201,169,97,0.12) 12px 24px)',
        border: '1.5px dashed var(--g33-or, #c9a961)',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        color: 'var(--g33-or-deep, #8E7A4A)',
      }}
    >
      <div
        className="g33-eyebrow"
        style={{ fontSize: 10, letterSpacing: '0.22em', marginBottom: 10, opacity: 0.7 }}
      >
        Image placeholder · {aspectRatio.replace(/\s/g, '')}
      </div>
      <div
        style={{
          fontFamily: 'var(--g33-font-mono, ui-monospace, "SF Mono", Consolas, monospace)',
          fontSize: 'clamp(11px, 1.4vw, 14px)',
          fontWeight: 600,
          letterSpacing: '0.04em',
          background: 'rgba(255,252,245,0.85)',
          padding: '8px 14px',
          border: '1px solid rgba(201,169,97,0.3)',
          borderRadius: 2,
          color: 'var(--g33-noir, #1a1a1a)',
          wordBreak: 'break-all',
          maxWidth: '90%',
        }}
      >
        {code}
      </div>
      {captionOverride && (
        <div
          style={{
            marginTop: 14,
            fontSize: 12,
            fontStyle: 'italic',
            color: 'var(--g33-grey-warm)',
            maxWidth: 480,
          }}
        >
          {captionOverride}
        </div>
      )}
    </div>
  );
};
// ============ Image swap modal (hero / inline images) ============
export const ImageSwapButton = ({ currentUrl, onSave, label = 'Image', testId = '' }) => {
  const { editMode } = useProgram();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(currentUrl || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setUrl(currentUrl || ''); setError(''); } }, [open, currentUrl]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image.'); return; }
    if (file.size > 8 * 1024 * 1024) { setError('Image too large (max 8MB).'); return; }
    setError(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/gate33-program/upload`, {
        method: 'POST',
        body: fd,
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`Upload failed (${r.status})`);
      const d = await r.json();
      // Backend returns a relative /api/... path; make it absolute to survive cross-origin deployments.
      const absUrl = d.url && d.url.startsWith('/') ? `${API}${d.url}` : d.url;
      setUrl(absUrl);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commit = async () => {
    setSaving(true);
    try { await onSave(url || ''); setOpen(false); }
    catch {} finally { setSaving(false); }
  };

  if (!editMode) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="g33-image-swap-btn"
        data-testid={testId || 'image-swap-btn'}
      >
        <Pencil size={13} /> Change image
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(false)}
            className="g33-modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="g33-modal"
              role="dialog"
              aria-modal="true"
              aria-label={`Edit ${label}`}
            >
              <div className="g33-modal-header">
                <div>
                  <div className="g33-eyebrow" style={{ marginBottom: 6 }}>Edit</div>
                  <h3 className="g33-display" style={{ fontSize: 24, margin: 0 }}>{label}</h3>
                </div>
                <button onClick={() => setOpen(false)} className="g33-modal-close" aria-label="Close">
                  <X size={18} />
                </button>
              </div>

              {url && (
                <div style={{
                  marginTop: 20, aspectRatio: '16 / 9', overflow: 'hidden',
                  background: 'var(--g33-cream-warm)', border: '1px solid var(--g33-ligne)',
                }}>
                  <img src={url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}

              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />

              <button
                onClick={onPick}
                className="g33-btn-ghost"
                disabled={uploading}
                style={{ marginTop: 16, padding: '14px 0', justifyContent: 'flex-start' }}
                data-testid="image-upload-pick"
              >
                {uploading ? <><Loader2 size={14} className="g33-spin" /> Uploading…</> : <><Upload size={14} /> Upload from device</>}
              </button>

              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 11, color: 'var(--g33-grey-warm)', textTransform: 'uppercase', letterSpacing: '0.18em', display: 'block', marginBottom: 8 }}>
                  Or paste an image URL
                </label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://images.unsplash.com/..."
                  className="g33-modal-input"
                  data-testid="image-url-input"
                />
              </div>

              {error && (
                <p style={{ marginTop: 12, color: 'var(--g33-alert)', fontSize: 13 }}>{error}</p>
              )}

              <div className="g33-modal-actions">
                <button onClick={() => setOpen(false)} className="g33-btn-ghost">Cancel</button>
                <button
                  onClick={commit}
                  className="g33-btn-primary"
                  disabled={saving || uploading}
                  style={{ padding: '14px 28px', fontSize: 11 }}
                  data-testid="image-modal-save"
                >
                  {saving ? <><Loader2 size={14} className="g33-spin" /> Saving</> : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
